import { useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button, Input, Typography, Card, Snackbar, Stack, LinearProgress, FormControl, FormLabel, IconButton } from "@mui/joy";
import { RemoveRedEye, Report } from '@mui/icons-material';

import LoadingSpinnerComponent from '../components/LoadingSpinnerComponent';

import logo from '../resources/logo.svg'
import footer_appinion from '../resources/footer_logo.png'

const networking = require('../Networking/AuthenticationAPI');

const ResetPassword = () => {
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [searchParams] = useSearchParams();
    const [newPassword, setNewPassword] = useState("");
    const [showPass, setShowPass] = useState(false);
    const [error, setError] = useState('');
    const [showError, setShowError] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);

    const navigate = useNavigate();
    const token = searchParams.get("token");

    const minLength = 8;
    let strengthLevel = 0;

    const isUpperCase = /[A-Z]/.test(newPassword);
    const isLowerCase = /[a-z]/.test(newPassword);
    const isNumber = /[0-9]/.test(newPassword);
    const isSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
    const isLengthValid = newPassword.length >= minLength;  // Minimum length validation

    if (isUpperCase) strengthLevel++;
    if (isLowerCase) strengthLevel++;
    if (isNumber) strengthLevel++;
    if (isSpecialChar) strengthLevel++;
    if (isLengthValid) strengthLevel++;

    const handleSubmit = async (event: any) => {
        event.preventDefault();
        setLoading(true);

        try {
            networking.reset(token, newPassword).then((data: any) => {
                setLoading(false);
                setShowSuccess(true);
                setMessage(data.message);

                setTimeout(() => {
                    navigate('/authenticate');
                }, 2000);

            }).catch((error: Error) => {
                setError(error.message);
                setShowError(true);
                setLoading(false);
            });

        } catch (error: any) {
            setError(error.message);
            setShowError(true);
            setLoading(false);
        }

    };

    return (
        <>
            {showError &&
                <Snackbar
                    variant="soft"
                    color="danger"
                    open={showError}
                    onClose={() => setShowError(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    startDecorator={<Report />}
                    endDecorator={
                        <Button
                            onClick={() => setShowError(false)}
                            size="sm"
                            variant="soft"
                            color="danger"
                        >
                            Dismiss
                        </Button>
                    }
                >
                    {error}
                </Snackbar>
            }
            {showSuccess &&
                <Snackbar
                    variant="soft"
                    color="success"
                    open={showSuccess}
                    onClose={() => setShowSuccess(false)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    startDecorator={<Report />}
                    endDecorator={
                        <Button
                            onClick={() => setShowSuccess(false)}
                            size="sm"
                            variant="soft"
                            color="success"
                        >
                            Dismiss
                        </Button>
                    }
                >
                    {message}
                </Snackbar>
            }
            <div className='container-fluid bg-cloudie p-5'>
                <div className='text-center'>
                    <a
                        href='/'>
                        <img
                            src={logo}
                            height={40 + 'px'}
                            alt='Appinion logo' />
                    </a>
                </div>
                <div className='mt-4 text-center'>
                    <Typography
                        level="title-lg"
                        sx={{
                            fontWeight: 'bold',
                            color: '#313131',
                            marginBottom: 1
                        }}>
                        Reset your password
                    </Typography>
                </div>
                <Card
                    sx={{
                        mx: 'auto', // margin left & right
                        my: 4, // margin top & bottom
                        py: 5, // padding top & bottom
                        px: 5, // padding left & right
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        borderRadius: 'sm',
                        boxShadow: 'md',
                        maxWidth: 600
                    }}
                    variant="outlined"
                >
                    {loading ?
                        <LoadingSpinnerComponent />
                        :
                        <form
                            onSubmit={handleSubmit}>
                            <Stack
                                spacing={4}
                                sx={{ '--hue': Math.min(strengthLevel * 10, 120) }}>
                                <FormControl>
                                    <FormLabel>New password</FormLabel>
                                    <Input
                                        name="password"
                                        type={showPass ? "text" : "password"}
                                        placeholder="Type in here…"
                                        endDecorator={
                                            <IconButton
                                                onClick={() => setShowPass(!showPass)}>
                                                {
                                                    showPass ?
                                                        <RemoveRedEye></RemoveRedEye>
                                                        :
                                                        <RemoveRedEye></RemoveRedEye>
                                                }
                                            </IconButton>
                                        }
                                        value={newPassword}
                                        onChange={(event) => setNewPassword(event.target.value)}
                                    />
                                    <LinearProgress
                                        determinate
                                        size="sm"
                                        value={Math.min((strengthLevel * 100) / 5, 100)}
                                        sx={{
                                            bgcolor: 'background.level3',
                                            color: 'hsl(var(--hue) 80% 40%)',
                                            marginTop: 2
                                        }}
                                    />
                                    <Typography
                                        level="body-xs"
                                        sx={{ alignSelf: 'flex-end', color: 'hsl(var(--hue) 80% 30%)' }}
                                    >
                                        {strengthLevel < 1 && 'Very weak'}
                                        {strengthLevel >= 1 && strengthLevel < 3 && 'Weak'}
                                        {strengthLevel >= 3 && strengthLevel < 4 && 'Medium'}
                                        {strengthLevel >= 4 && strengthLevel < 5 && 'Strong'}
                                        {strengthLevel >= 5 && 'Very strong'}
                                    </Typography>
                                </FormControl>
                                <Button
                                    type='submit'
                                    sx={{ mt: 1 /* margin top */ }}>
                                    Reset password
                                </Button>
                            </Stack>
                        </form>
                    }
                </Card>
                <div className="mt-4 text-center">
                    <Typography
                        level="body-sm"
                        color="neutral"
                        component="h1">
                        <a href='/authenticate'>
                            <span
                                className='underlined'>Back to sign in</span>
                        </a>
                    </Typography>
                </div>
            </div>
            <footer className='p-3 text-center'>
                <p className='legend light-iron'>© Appinion 2025. All rights reserved</p>
                <img className='ms-3' src={footer_appinion} alt="logo" style={{ height: 16 + 'px' }}></img>
                <ul className='navbar-nav ms-auto'>
                    <li className='nav-item'>
                    </li>
                </ul>
            </footer>
        </>
    );
};

export default ResetPassword;